import React from "react"
import {
  Box,
  Typography,
  MenuItem,
  Select,
  SelectChangeEvent,
  IconButton,
  Button,
  Menu,
  CircularProgress,
  styled,
  FormControl,
} from "@mui/material"
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank"
import CheckBoxIcon from "@mui/icons-material/CheckBox"

import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import {
  setCopyTo,
  setCopyToAll,
  setCopyToOptions,
  unsetCopyTo,
} from "../../redux/slices/resourceBudgetSlice"
import { colors } from "../../../styles/commonStyles"
import { filtersType, forecastFilterTypes, resourceBudgetType } from "./types"

import { getTokenLocal } from "../../../helper/utils"
import { strFormat } from "../../components/Table/ResourceBudgetTable/utils"
import {
  copyOptions,
  setCopyOptions,
} from "../../components/Table/ResourceBudgetTable/apiUtils"
import { AlertModal } from "../../components/Tabs/ResourceBudgetTabs/Atom"

type filterProps = {
  disable?: boolean
  label?: string
  filterHandler: any
  filters: any
  index: string
  apiCall: (selectedFilter: any) => null | undefined
}
type dateSliderProps = {
  month: number
  year: any
  monthIncrement: any
  monthDecrement: any
  dirty: boolean
}
type budgetCopyTo = {
  open: boolean
  close: any
  onClick: any
  anchorEl: any
  label: string
}

type monthsDropdownProps = {
  anchorEl: any
  open: boolean
  close: any
  initial: boolean
  setInitial: any
}

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

export const Filters = ({
  label,
  filterHandler,
  filters,
  apiCall,
  index,
  disable,
}: filterProps) => {
  const { data, accessKey, selected } = filters
  const dispatch = useAppDispatch()
  const handleFilters = (e: SelectChangeEvent) => {
    dispatch(filterHandler({ label: index, val: e.target.value }))
    apiCall({ val: e.target.value })
  }

  return (
    <Box
      sx={{
        marginX: "6px",
      }}
    >
      {label && <Typography fontSize={"10px"}>{label}</Typography>}
      <FormControl>
        <Select
          disabled={disable}
          sx={{
            height: "40px",
            width: "200px",
            fontSize: "14px",
            mt: "5px",
            backgroundColor: "#fff",
          }}
          IconComponent={ExpandMoreIcon}
          value={selected}
          onChange={handleFilters}
          MenuProps={{
            sx: {
              marginTop: "5px",
            },
          }}
        >
          {data?.map((item: any, index: number) => (
            <MenuItem
              key={`filter-item-${index}`}
              sx={{
                minHeight: "40px",
                mx: "11px",
                my: "5px",
                borderRadius: "5px",
                fontSize: "14px",
                width: "180px",
                whiteSpace: "normal",
                py: 1.2,
              }}
              value={accessKey ? item[accessKey] : item}
            >
              {accessKey ? item[accessKey] : item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

export const DateSlider = (props: dateSliderProps) => {
  const { month, year, monthIncrement, monthDecrement, dirty } = props
  const [showModel, setshowModel] = React.useState<{
    open: boolean
    increment: null | boolean
  }>({
    open: false,
    increment: null,
  })
  const dispatch = useAppDispatch()

  const monthSwitchHandler = (increment: boolean) => {
    if (dirty) {
      return setshowModel({ open: true, increment })
    }
    if (increment) {
      dispatch(monthIncrement())
    } else {
      dispatch(monthDecrement())
    }
  }
  const confirmActionHandler = () => {
    if (showModel.increment) {
      dispatch(monthIncrement())
    } else {
      dispatch(monthDecrement())
    }
    setshowModel({ open: false, increment: null })
  }
  const modelCloseHandler = () => {
    setshowModel((prev) => ({ ...prev, open: false }))
  }
  return (
    <Box
      sx={{
        bgcolor: "#3366D6",
        display: "flex",
        width: "220px",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "4px",
        color: "white",
      }}
    >
      <IconButton
        disabled={month === 1}
        onClick={() => monthSwitchHandler(false)}
      >
        <KeyboardArrowLeftIcon
          sx={{
            color: month === 1 ? colors.disabled : "white",
          }}
        />
      </IconButton>
      <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
        {months[month - 1]} {year ? year?.label : ""}
      </Typography>
      <IconButton
        disabled={month === 12}
        onClick={() => monthSwitchHandler(true)}
      >
        <KeyboardArrowRightIcon
          sx={{
            color: month === 12 ? colors.disabled : "white",
          }}
        />
      </IconButton>
      <AlertModal
        close={modelCloseHandler}
        open={showModel.open}
        title={"Month change alert"}
        optionalMessage={
          "You've unsaved data in current tab and switching month can cause data loss"
        }
        buttonText="Proceed anyway"
        confirmActionHandler={confirmActionHandler}
      />
    </Box>
  )
}

const MonthsDropdown = ({
  anchorEl,
  open,
  close,
  initial,
  setInitial,
}: monthsDropdownProps) => {
  const [loading, setLoading] = React.useState<boolean>(false)
  const [copying, setCopying] = React.useState<boolean>(false)
  const dispatch = useAppDispatch()
  const token = getTokenLocal()
  const state: resourceBudgetType = useAppSelector(
    (state) => state.resourceBudget,
  )
  const { primaryIndex, primaryTabs, copyTo } = state
  const selectedMonth = state.month
  const office = useAppSelector(
    (store: any) => store.metric.maevaSelectedPracticesNoAll?.[0],
  )
  React.useEffect(() => {
    if (initial) {
      const computedKey = primaryTabs[primaryIndex]
      copyOptions[computedKey]({
        year: state.selectedYear,
        office,
        token,
        loading: setLoading,
        dispatch: (options: any) => {
          dispatch(setCopyToOptions(options))
          setInitial(false)
        },
      })
    }
  }, [])

  const copyToHandler = () => {
    setCopyOptions({
      dispatch: () => {
        close()
        dispatch(setCopyToAll(false))
      },
      token,
      loading: (loadVal: boolean) => setCopying(loadVal),
      office,
      year: state.selectedYear,
      month: state.month,
      state,
    })
  }
  return (
    <Menu anchorEl={anchorEl} open={open} onClose={close} sx={{ mt: 0.5 }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            py: 2,
            minWidth: "250px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
          <Typography sx={{ my: 2 }}>Loading...</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            minWidth: "250px",
          }}
        >
          <MenuItem
            onClick={() => {
              dispatch(
                copyTo.selectAll ? setCopyToAll(false) : setCopyToAll(true),
              )
            }}
            key={`select-all-op`}
            sx={{
              mb: "3px",
              mx: "3px",
            }}
          >
            {copyTo.selectAll ? (
              <CheckBoxIcon
                sx={{
                  color: colors.primaryBlue,
                }}
              />
            ) : (
              <CheckBoxOutlineBlankIcon
                sx={{
                  color: colors.primaryBlue,
                }}
              />
            )}
            <Typography
              sx={{
                pl: "5px",
                fontSize: "14px",
              }}
            >
              Select all
            </Typography>
          </MenuItem>
          {Object.keys(copyTo.data).map((month: string, index: number) => {
            const current = index === selectedMonth - 1
            const monthIndex = copyTo.data[month]?.month
            return (
              <MenuItem
                disabled={current}
                onClick={() => {
                  copyTo.selected.includes(monthIndex)
                    ? dispatch(unsetCopyTo(monthIndex))
                    : dispatch(setCopyTo(monthIndex))
                }}
                key={`month-dd-${index}`}
                sx={{
                  mb: "3px",
                  mx: "3px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", flex: 1 }}>
                  {copyTo.selected.includes(monthIndex) || current ? (
                    <CheckBoxIcon
                      sx={{
                        color: current ? colors.grey200 : colors.primaryBlue,
                      }}
                    />
                  ) : (
                    <CheckBoxOutlineBlankIcon
                      sx={{
                        color: colors.primaryBlue,
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      pl: "5px",
                      fontSize: "14px",
                    }}
                  >
                    {strFormat(month)}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    // pl: "5px",
                    fontSize: "8px",
                    color: "orange",
                  }}
                >
                  {copyTo.data[month]?.budget_exists && !current
                    ? "Budget Exists"
                    : ""}
                </Typography>
              </MenuItem>
            )
          })}
          <Button
            onClick={copyToHandler}
            endIcon={
              copying ? (
                <CircularProgress size="1.2rem" sx={{ color: "#fff" }} />
              ) : undefined
            }
            variant="contained"
            sx={{
              display: "flex",
              borderRadius: "4px",
              backgroundColor: "#3366D6",
              color: "#fff",
              mx: "13px",
              my: "10px",
            }}
          >
            Copy & Apply
          </Button>
        </Box>
      )}
    </Menu>
  )
}

export const BudgetCopyTo = ({
  open,
  close,
  onClick,
  anchorEl,
  label,
}: budgetCopyTo) => {
  const [initial, setInitial] = React.useState<boolean>(false)
  return (
    <Box>
      <Button
        variant="outlined"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={(e) => {
          onClick(e)
          setInitial(true)
        }}
        endIcon={<KeyboardArrowDownIcon sx={{ color: "#000" }} />}
        sx={{
          minWidth: "250px",
          textTransform: "none",
          py: "7px",
        }}
      >
        Copy {label} budget to
      </Button>
      {open && (
        <MonthsDropdown
          anchorEl={anchorEl}
          open={open}
          close={close}
          initial={initial}
          setInitial={setInitial}
        />
      )}
    </Box>
  )
}

export const MetricHeaderWrapper = styled(Box)({
  width: "100%",
  height: "10vh",
  backgroundColor: "#F8F9FA",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: "15px 30px",
})
